import type { Product, Shop } from "@/lib/TicketApi";

/**
 * FL-581 — tiered ticketing. Whether this ticket still waits for the ticket it
 * goes on sale after (Early bird → Regular → Late bird): it does while that one
 * can still be bought. It opens when that one is all reserved or sold out, its
 * own sale end has passed, or it is gone (archived / not for sale), and shuts
 * again when those reservations lapse. A status this build does not know keeps
 * it shut. ticket-api refuses to reserve a waiting ticket too.
 */
export function waitsForPreviousTier(product: Product, now: number = Date.now()): boolean {
    if (!product.availableAfterProductId || product.availableAfterClosed) {
        return false;
    }
    const status = product.availableAfterInventoryStatus ?? "available";
    if (status === "all_reserved" || status === "sold_out") {
        return false;
    }
    const endsAt = product.availableAfterSaleEndsAt ? Date.parse(product.availableAfterSaleEndsAt) : NaN;
    return !(Number.isFinite(endsAt) && endsAt < now);
}

/**
 * Whether any product in the shop waits for another, so the shop is worth
 * refreshing: the next tier must open as soon as the one before it is gone.
 * The lists are ordered objects on the wire, hence Object.values.
 */
export function shopHasTieredProducts(shop: Shop): boolean {
    return Object.values(shop.events).some((event) =>
        Object.values(event.productPages).some((page) =>
            Object.values(page.productSections).some((section) =>
                Object.values(section.products).some((product) => !!product.availableAfterProductId),
            ),
        ),
    );
}
